import BaseCommunicator from "./BaseCommunicator";
import { DatalabPostMessageType, TDatalabCommunicatorData, IParentToIframeCommunicator, IReportToDatalabCommunicator } from './types';


/**
 * 父窗口与iframe通信的基类
 * 提供基本的消息发送和接收功能，支持跨域通信
 */
class ParentToIframeCommunicator extends BaseCommunicator implements IParentToIframeCommunicator {
  private _iframe: HTMLIFrameElement | null = null;
  private readonly iframeSelector: string = '';

  protected constructor(iframeSelector: string) {
    super();
    if (iframeSelector) {
      this.iframeSelector = iframeSelector;
      // 明确指定 querySelector 返回的 HTMLIFrameElement 类型，并检查 iframe 是否存在
      const iframe = document.querySelector<HTMLIFrameElement>(iframeSelector);
      if (iframe) {
        this._iframe = iframe;
      } else {
        console.warn(`Iframe元素未找到, ParentToIframeCommunicator的iframe私有属性未设置`);
      }
    }
  }

  /**
   * 向 iframe 发送消息（支持跨域）
   * @param type - 消息类型，用于子类区分处理逻辑
   * @param payload - 可选的消息负载数据
   * @param targetOrigin - 指定目标域（默认当前 iframe 的 src 域）
   */
  public sendToIframe(type: string, payload?: unknown, targetOrigin?: string): void {
    if (!this._iframe?.contentWindow) {
      const iframe = document.querySelector<HTMLIFrameElement>(this.iframeSelector);
      if (iframe) {
        this._iframe = iframe;
      } else {
        console.error(this.iframeSelector, ' iframe 未加载或不存在 contentWindow');
        return;
      }
    }

    try {
      // 计算安全的目标域
      const origin = targetOrigin || "*";
      this._iframe?.contentWindow?.postMessage({ type, payload }, origin);
    } catch (error) {
      console.error('向 iframe 发送消息失败:', error);
    }
  }

  public get iframe(): HTMLIFrameElement | null {
    return this._iframe;
  }
}

/**
 * 实现父窗口与datalab iframe通信的具体类
 * 采用单例模式，每个iframe选择器对应一个通信器实例
 */
export default class ReportToDatalabCommunicator extends ParentToIframeCommunicator implements IReportToDatalabCommunicator {
  // 单例实例缓存，key为iframe选择器
  private static instances = new Map<string, ReportToDatalabCommunicator>();

  /**
   * 获取单例实例（基于iframe选择器）
   * @param iframeSelector iframe的选择器字符串
   * @returns 对应iframe选择器的通信器实例
   */
  public static getInstance(iframeSelector: string): ReportToDatalabCommunicator {
    if (!ReportToDatalabCommunicator.instances.has(iframeSelector)) {
      ReportToDatalabCommunicator.instances.set(
        iframeSelector,
        new ReportToDatalabCommunicator(iframeSelector)
      );
    }
    return ReportToDatalabCommunicator.instances.get(iframeSelector)!;
  }

  /**
   * 销毁单例实例
   * @param iframeSelector 要销毁的iframe选择器对应的实例
   */
  public static destroyInstance(iframeSelector: string): void {
    const instance = ReportToDatalabCommunicator.instances.get(iframeSelector);
    if (instance) {
      instance.destroy(); // 销毁实例中的注册事件
      ReportToDatalabCommunicator.instances.delete(iframeSelector);
    }
  }

  private _token: string | undefined;  // 通过接口获取回来的token
  private _dashboardName: string | undefined;  // 当前承接页面报表的名称
  private _gameCode: string | undefined;  // 当前承接页面报表的所在业务
  private _dashboardId: string | undefined;       // 当前承接页面报表的dashboard ID
  private _origin: string | undefined;        // 当前承接页面报表的域名

  /**
   * 构造函数
   * @param iframeSelector iframe的选择器字符串
   */
  constructor(iframeSelector: string) {
    super(iframeSelector);
  }

  public get token(): string | undefined {
    return this._token;
  }

  public get dashboardId(): string | undefined {
    return this._dashboardId;
  }
  public get dashboardName(): string | undefined {
    return this._dashboardName;
  }
  public get gameCode(): string | undefined {
    return this._gameCode;
  }

  public get origin(): string | undefined {
    return this._origin;
  }

  /**
   * 保存通信数据
   * @param data 包含token、dashboardId和host等信息的通信数据
   */
  public saveData(data: TDatalabCommunicatorData): void {
    this._token = data?.token;
    this._dashboardId = data?.dashboardId;
    this._dashboardName = data?.dashboardName;
    this._gameCode = data?.gameCode;
    this._origin = data?.origin;
  }

  /**
   * 通知iframe更新token等数据
   * @param data 包含新token等的通信数据
   */
  public updateToken(data: TDatalabCommunicatorData): void {
    this.saveData(data);
    this.sendToIframe(DatalabPostMessageType.UpdateToken, data);
  }

  /**
   * 接收到iframe组件加载完成信息后的 处理事件
   * 向iframe发送初始化数据，包括token、dashboardId和host
   */
  public initData(data: TDatalabCommunicatorData): void {
    this.saveData(data);
    this.sendToIframe(DatalabPostMessageType.InitData, data);
  }

  /**
   * 向iframe发送语言切换信息
   * @param payload language 语言
   */
  public changeLanguage(payload: { language: string; }): void {
    this.sendToIframe(DatalabPostMessageType.ChangeLanguage, payload);
  }
  
  /**
   * 向iframe发送主题信息
   * @param payload  name是主题名称, invert是css filter: invert(数值)
   */
  public changeTheme(payload: { name: string; invert?: string; }): void {
    this.sendToIframe(DatalabPostMessageType.ChangeTheme, payload);
  }

}